import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import Location from '../../hooks/Location'
import { usePageConfig } from '../../hooks/usePageConfig'
import PageHeader from '../../components/layout/PageHeader'
import CompagnyMeta from '../../components/portfolio/CompagnyMeta'
import EffectDatabase from '../../components/meetstandaard/EffectDatabase'
import buildActivityProfile from '../../helpers/dataHelpers/buildActivityProfile'
import getBenchmarkStrength from '../../helpers/dataHelpers/getBenchmarkStrength'
import { useFirestoreNoCompagny } from '../../firebase/useFirestore'
import {
    activitySubtypeLabels,
    activitySubtypesByType,
    activityTypeLabels,
    deliveryModeLabels,
} from '../../helpers/dataHelpers/activityCategories'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined'
import TrendingFlatOutlinedIcon from '@mui/icons-material/TrendingFlatOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import Tooltip from '../../components/common/Tooltip'

// ─── Sub-components ───────────────────────────────────────────────────────────

const EvidenceBar = ({ score, max = 3 }) => (
    <div className='bcd-evidence-bar'>
        {Array.from({ length: max + 1 }).map((_, i) => (
            <div
                key={i}
                className={`bcd-evidence-bar-segment${i <= score ? ' bcd-evidence-bar-segment--filled' : ''}`}
            />
        ))}
    </div>
)

const confidenceColor = (score) => {
    if (score >= 3) return '#38a169'
    if (score >= 2) return '#d69e2e'
    if (score >= 1) return '#dd6b20'
    return '#e53e3e'
}

const DeltaBadge = ({ delta, large = false }) => {
    const cls = large ? 'bcd-delta bcd-delta--large' : 'bcd-delta'
    if (delta == null) return <span className={`${cls} bcd-delta--neutral`}>—</span>
    if (delta > 0) return (
        <span className={`${cls} bcd-delta--positive`}>
            <TrendingUpOutlinedIcon style={{ fontSize: large ? 22 : 16 }} />
            +{delta}%
        </span>
    )
    if (delta < 0) return (
        <span className={`${cls} bcd-delta--negative`}>
            <TrendingDownOutlinedIcon style={{ fontSize: large ? 22 : 16 }} />
            {delta}%
        </span>
    )
    return (
        <span className={`${cls} bcd-delta--neutral`}>
            <TrendingFlatOutlinedIcon style={{ fontSize: large ? 22 : 16 }} />
            0%
        </span>
    )
}

// Effort badge pill
const EffortPill = ({ value }) => {
    const colors = { low: '#d69e2e', medium: '#3182ce', high: '#38a169' }
    const labels = { low: 'Laag', medium: 'Middel', high: 'Hoog' }
    return (
        <span className='bcd-pill' style={{ background: colors[value] || '#aaa' }}>
            {labels[value] || value}
        </span>
    )
}

// ─── Main component ───────────────────────────────────────────────────────────

const BenchmarkCompanyDetail = () => {
    const history = useHistory()
    const { getPageConfig } = usePageConfig()
    const companyId = Location()[3]
    const effectId = Location()[4]
    const effectDatabase = EffectDatabase()

    const effectName = effectDatabase && effectDatabase
        .flatMap(category => category.effects || [])
        .find(effect => effect.id === effectId)?.name || ''

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [result, setResult] = useState(null)

    // Activities for this specific org
    const orgActivities = useFirestoreNoCompagny('Activities', 'CompagnyID', companyId || '')

    useEffect(() => {
        if (!effectId) return

        let cancelled = false
        const load = async () => {
            setLoading(true)
            setError('')
            setResult(null)

            const msiBenchmark = firebase.app().functions('europe-west1').httpsCallable('msiBenchmark')
            const response = await msiBenchmark({ msiId: effectId })
            if (!cancelled) {
                setResult(response.data || {})
                setLoading(false)
            }
        }

        load().catch((err) => {
            console.error('BenchmarkCompanyDetail error:', err)
            if (!cancelled) {
                setError('Gegevens konden niet worden geladen.')
                setLoading(false)
            }
        })

        return () => { cancelled = true }
    }, [effectId])

    // ─── Derived data ─────────────────────────────────────────────────────────

    const allCompanies = result?.respondentsPerCompany || []
    const aggregate = result?.benchmarkGrowth ?? null
    const thisCompany = allCompanies.find(c => c.companyId === companyId) || null

    const ownGrowth = thisCompany?.benchmarkGrowth ?? null
    const deltaVsBenchmark = ownGrowth != null && aggregate != null
        ? Math.round((ownGrowth - aggregate) * 10) / 10
        : null

    // Rank
    const sorted = [...allCompanies].sort((a, b) => (b.benchmarkGrowth ?? -Infinity) - (a.benchmarkGrowth ?? -Infinity))
    const rank = sorted.findIndex(c => c.companyId === companyId) + 1
    const total = allCompanies.length

    // Min / max for position bar
    const growthValues = allCompanies.map(c => c.benchmarkGrowth).filter(v => v != null)
    const minGrowth = growthValues.length ? Math.min(...growthValues) : null
    const maxGrowth = growthValues.length ? Math.max(...growthValues) : null

    // Position on bar as percentage (0–100)
    const barPosition = (ownGrowth != null && minGrowth != null && maxGrowth != null && maxGrowth !== minGrowth)
        ? Math.round(((ownGrowth - minGrowth) / (maxGrowth - minGrowth)) * 100)
        : null

    // Evidence
    const strength = result
        ? getBenchmarkStrength({
            companyCount: result.companyCount ?? allCompanies.length,
            respondentCount: result.respondentCount ?? 0,
            respondentsPerCompany: allCompanies,
        })
        : null

    // Peers — top 3 with highest growth (above average) for comparison
    const topPerformers = [...allCompanies]
        .filter(c => c.companyId !== companyId && c.benchmarkGrowth != null)
        .sort((a, b) => b.benchmarkGrowth - a.benchmarkGrowth)
        .slice(0, 3)

    // Activity profile for this org
    const activityProfile = buildActivityProfile(orgActivities)
    const hasActivityData = activityProfile.length > 0
    const subtypeCounts = (orgActivities || []).reduce((acc, activity) => {
        const subtype = activity.activitySubtype
        if (!subtype) return acc

        acc[subtype] = (acc[subtype] || 0) + 1
        return acc
    }, {})

    // Dominant effort across all activities
    const allEfforts = (orgActivities || []).map(a => a.estimatedEffort).filter(Boolean)
    const effortCounts = { low: 0, medium: 0, high: 0 }
    allEfforts.forEach(e => { if (effortCounts[e] != null) effortCounts[e]++ })
    const dominantEffort = allEfforts.length
        ? Object.entries(effortCounts).sort((a, b) => b[1] - a[1])[0][0]
        : null

    // Context sentence
    const contextSentence = (() => {
        if (ownGrowth == null) return null
        const dir = deltaVsBenchmark > 0 ? 'boven' : deltaVsBenchmark < 0 ? 'onder' : 'op'
        const resCount = thisCompany?.respondentCount ?? 0
        const strengthStr = strength ? strength.helperPrefix.toLowerCase() : ''
        return `Scoort ${Math.abs(deltaVsBenchmark ?? 0)}% ${dir} de benchmark · ${resCount} respondenten · ${strengthStr}`
    })()

    const activityDetailRoute = (activityKey) =>
        `/${client}/benchmark/activity/${activityKey}`
    const benchmarkCompanyPageConfig = getPageConfig('benchmarkcompanydetail')

    return (
        <div className='main'>
            <div className='main-container'>
                <div className='benchmark-details-container'>

                    {/* ── Back ── */}
                    <div
                        className='bcd-back'
                        onClick={() => history.push(`/${client}/benchmarkdetails/${effectId}`)}
                    >
                        <ArrowBackOutlinedIcon style={{ fontSize: 16 }} />
                        Terug naar {benchmarkCompanyPageConfig.backLabel || 'benchmark overzicht'}
                    </div>

                    <PageHeader
                        heading={<CompagnyMeta id={companyId} />}
                        subHeading={effectName}
                        description={contextSentence || 'Benchmark analyse voor deze organisatie op dit effect.'}
                        AI={false}
                    />

                    {loading && <p className='bcd-loading'>Gegevens worden geladen…</p>}
                    {error && <p className='bcd-error'>{error}</p>}

                    {!loading && !error && result && (
                        <div className='bcd-layout'>

                            {/* ════════════════════════════════════════════
                                BLOK 1 — Score & context
                            ════════════════════════════════════════════ */}
                            <div className='bcd-block bcd-block--hero'>
                                <p className='bcd-block-label'>Score & context</p>

                                <div className='bcd-hero-row'>

                                    {/* Main score */}
                                    <div className='bcd-hero-score'>
                                        <span className='bcd-hero-value'>
                                            {ownGrowth != null ? `${ownGrowth}%` : '—'}
                                        </span>
                                        <span className='bcd-score-sub'>groei op dit effect</span>
                                    </div>

                                    <div className='bcd-hero-divider' />

                                    {/* Delta */}
                                    <div className='bcd-hero-score'>
                                        <DeltaBadge delta={deltaVsBenchmark} large />
                                        <span className='bcd-score-sub'>t.o.v. benchmark ({aggregate != null ? `${aggregate}%` : '—'})</span>
                                    </div>

                                    <div className='bcd-hero-divider' />

                                    {/* Rank */}
                                    <div className='bcd-hero-score'>
                                        <span className='bcd-hero-value'>
                                            {rank > 0 ? `#${rank}` : '—'}
                                            <span className='bcd-hero-value-sub'> / {total}</span>
                                        </span>
                                        <span className='bcd-score-sub'>positie in benchmark</span>
                                    </div>

                                    <div className='bcd-hero-divider' />

                                    {/* Respondents */}
                                    <div className='bcd-hero-score'>
                                        <span className='bcd-hero-value'>
                                            {thisCompany?.respondentCount ?? '—'}
                                        </span>
                                        <span className='bcd-score-sub'>respondenten</span>
                                    </div>

                                </div>

                                {/* Evidence strip */}
                                {strength && (
                                    <div className='bcd-evidence-strip'>
                                        <Tooltip content='Gebaseerd op aantal organisaties, respondenten en spreiding' top='-55px'>
                                            <span
                                                className='bcd-strength-badge'
                                                style={{ color: confidenceColor(strength.finalScore) }}
                                            >
                                                ● {strength.label}
                                            </span>
                                        </Tooltip>
                                        <span className='bcd-evidence-strip-detail'>
                                            {result.companyCount ?? allCompanies.length} organisaties ·
                                            {result.respondentCount ?? 0} respondenten ·
                                            data is {strength.spreadLabel}
                                        </span>
                                        <div className='bcd-evidence-bar-row'>
                                            <EvidenceBar score={strength.finalScore} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ════════════════════════════════════════════
                                BLOK 2 — Positie t.o.v. anderen
                            ════════════════════════════════════════════ */}
                            <div className='bcd-block'>
                                <p className='bcd-block-label'>Positie in het veld</p>

                                {barPosition != null && minGrowth != null && maxGrowth != null ? (
                                    <>
                                        <div className='bcd-position-bar-wrap'>
                                            <div className='bcd-position-bar'>
                                                {/* Gemiddelde lijn */}
                                                {aggregate != null && maxGrowth !== minGrowth && (
                                                    <Tooltip content={`Benchmark gemiddelde: ${aggregate}%`} top='-50px'>
                                                        <div
                                                            className='bcd-position-avg-line'
                                                            style={{ left: `${Math.round(((aggregate - minGrowth) / (maxGrowth - minGrowth)) * 100)}%` }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {/* Eigen positie */}
                                                <Tooltip content={`Jouw score: ${ownGrowth}%`} top='-50px'>
                                                    <div
                                                        className='bcd-position-marker'
                                                        style={{ left: `${barPosition}%` }}
                                                    />
                                                </Tooltip>
                                            </div>
                                            <div className='bcd-position-bar-labels'>
                                                <span>{minGrowth}% (laagste)</span>
                                                <span>{maxGrowth}% (hoogste)</span>
                                            </div>
                                        </div>

                                        <div className='bcd-position-legend'>
                                            <span className='bcd-legend-item'>
                                                <span className='bcd-legend-dot bcd-legend-dot--marker' />
                                                Jij ({ownGrowth}%)
                                            </span>
                                            <span className='bcd-legend-item'>
                                                <span className='bcd-legend-dot bcd-legend-dot--avg' />
                                                Gemiddelde ({aggregate}%)
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <p className='bcd-empty'>Onvoldoende data voor positiebepaling.</p>
                                )}

                                {/* Top 3 */}
                                {topPerformers.length > 0 && (
                                    <div className='bcd-top-list'>
                                        <p className='bcd-top-list-label'>Top scorers</p>
                                        {topPerformers.map((p, i) => {
                                            const pDelta = p.benchmarkGrowth != null && aggregate != null
                                                ? Math.round((p.benchmarkGrowth - aggregate) * 10) / 10
                                                : null
                                            return (
                                                <div key={p.companyId} className='bcd-top-list-row'>
                                                    <span className='bcd-top-rank'>#{i + 1}</span>
                                                    <span
                                                        className='benchmark-details-company-link'
                                                        onClick={() => history.push(`/${client}/benchmarkcompanydetail/${p.companyId}/${effectId}`)}
                                                    >
                                                        <CompagnyMeta id={p.companyId} />
                                                    </span>
                                                    <span className='bcd-top-score'>{p.benchmarkGrowth}%</span>
                                                    <DeltaBadge delta={pDelta} />
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* ════════════════════════════════════════════
                                BLOK 3 — Jouw profiel
                            ════════════════════════════════════════════ */}
                            <div className='bcd-block'>
                                <p className='bcd-block-label'>Jouw interventieprofiel</p>

                                {!hasActivityData && (
                                    <p className='bcd-empty'>
                                        Geen activiteiten gevonden voor deze organisatie.
                                    </p>
                                )}

                                {hasActivityData && (
                                    <>
                                        <div className='bcd-profile-list'>
                                            {activityProfile.map((item) => (
                                                <div key={item.type} className='bcd-profile-row'>
                                                    <button
                                                        type='button'
                                                        className='bcd-profile-type bcd-profile-type--button'
                                                        onClick={() => history.push(activityDetailRoute(item.type))}
                                                    >
                                                        <span>{activityTypeLabels[item.type] || item.type}</span>
                                                        {item.count > 1 && (
                                                            <span className='bcd-profile-count'>{item.count}×</span>
                                                        )}
                                                    </button>
                                                    <div className='bcd-profile-meta'>
                                                        <EffortPill value={item.dominantEffort} />
                                                        {item.uniqueModes.slice(0, 2).map(m => (
                                                            <span key={m} className='bcd-profile-mode'>
                                                                {deliveryModeLabels[m] || m}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <div className='bcd-profile-subtypes'>
                                                        {(activitySubtypesByType[item.type] || [])
                                                            .filter((subtype) => subtypeCounts[subtype])
                                                            .map((subtype) => (
                                                                <button
                                                                    key={subtype}
                                                                    type='button'
                                                                    className='bcd-profile-subtype-button'
                                                                    onClick={() => history.push(activityDetailRoute(subtype))}
                                                                >
                                                                    {activitySubtypeLabels[subtype] || subtype}
                                                                    <span className='bcd-profile-subtype-count'>
                                                                        {subtypeCounts[subtype]}×
                                                                    </span>
                                                                </button>
                                                            ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {dominantEffort && (
                                            <div className='bcd-profile-summary'>
                                                <span className='bcd-profile-summary-label'>Dominante intensiteit:</span>
                                                <EffortPill value={dominantEffort} />
                                                <span className='bcd-profile-summary-label' style={{ marginLeft: 12 }}>
                                                    {orgActivities.length} activiteit{orgActivities.length !== 1 ? 'en' : ''}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* ════════════════════════════════════════════
                                BLOK 4 — Vergelijking met peers
                            ════════════════════════════════════════════ */}
                            <div className='bcd-block bcd-block--wide'>
                                <p className='bcd-block-label'>Vergelijking met top performers</p>

                                <div className='bcd-compare-grid'>

                                    {/* This org column */}
                                    <div className='bcd-compare-col bcd-compare-col--this'>
                                        <p className='bcd-compare-col-header'>
                                            <CompagnyMeta id={companyId} />
                                            <span className='bcd-this-org'> (deze org)</span>
                                        </p>

                                        {hasActivityData ? (
                                            <ul className='bcd-compare-list'>
                                                {activityProfile.slice(0, 4).map(item => (
                                                    <li
                                                        key={item.type}
                                                        className='bcd-compare-list-item bcd-compare-list-item--button'
                                                        onClick={() => history.push(activityDetailRoute(item.type))}
                                                    >
                                                        <EffortPill value={item.dominantEffort} />
                                                        {activityTypeLabels[item.type] || item.type}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className='bcd-empty'>Geen activiteitsdata</p>
                                        )}

                                        <div className='bcd-compare-score'>
                                            <span className='bcd-compare-score-value'>
                                                {ownGrowth != null ? `${ownGrowth}%` : '—'}
                                            </span>
                                            <DeltaBadge delta={deltaVsBenchmark} />
                                        </div>
                                    </div>

                                    {/* Top performers columns */}
                                    {topPerformers.map((peer, i) => {
                                        const pDelta = peer.benchmarkGrowth != null && aggregate != null
                                            ? Math.round((peer.benchmarkGrowth - aggregate) * 10) / 10
                                            : null

                                        return (
                                            <div key={peer.companyId} className='bcd-compare-col'>
                                                <p
                                                    className='bcd-compare-col-header benchmark-details-company-link'
                                                    onClick={() => history.push(`/${client}/benchmarkcompanydetail/${peer.companyId}/${effectId}`)}
                                                >
                                                    <CompagnyMeta id={peer.companyId} />
                                                </p>

                                                {/* Dummy activity profile for peers — real data not yet available cross-org */}
                                                <ul className='bcd-compare-list'>
                                                    <li className='bcd-compare-list-item bcd-compare-list-item--dummy'>
                                                        <span className='bcd-dummy-label'>
                                                            Interventieprofiel beschikbaar zodra organisatie data deelt
                                                        </span>
                                                    </li>
                                                </ul>

                                                <div className='bcd-compare-score'>
                                                    <span className='bcd-compare-score-value'>
                                                        {peer.benchmarkGrowth}%
                                                    </span>
                                                    <DeltaBadge delta={pDelta} />
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {topPerformers.length === 0 && (
                                        <div className='bcd-compare-col'>
                                            <p className='bcd-empty'>Geen peers beschikbaar voor vergelijking.</p>
                                        </div>
                                    )}

                                </div>
                            </div>

                            {/* ════════════════════════════════════════════
                                BLOK 5 — AI uitleg (placeholder)
                            ════════════════════════════════════════════ */}
                            <div className='bcd-block bcd-block--wide bcd-block--ai'>
                                <p className='bcd-block-label'>
                                    <AutoAwesomeOutlinedIcon style={{ fontSize: 14 }} />
                                    Eerste verklaring
                                </p>

                                {deltaVsBenchmark != null && (
                                    <div className='bcd-ai-insight'>
                                        <p className='bcd-ai-text'>
                                            {deltaVsBenchmark > 5 && (
                                                <>
                                                    Deze organisatie scoort significant boven de benchmark op <strong>{effectName}</strong>.
                                                    {dominantEffort === 'high' && ' Het profiel laat een hoge interventie-intensiteit zien, wat mogelijk samenhangt met de bovengemiddelde score.'}
                                                    {dominantEffort === 'medium' && ' De activiteiten hebben overwegend een gemiddelde intensiteit. Verdere analyse kan aantonen welke specifieke combinaties bijdragen.'}
                                                    {dominantEffort === 'low' && ' Opvallend: hoge score ondanks relatief lichte interventies. Dit kan interessant zijn om verder te onderzoeken.'}
                                                </>
                                            )}
                                            {deltaVsBenchmark <= 5 && deltaVsBenchmark >= -5 && (
                                                <>
                                                    Deze organisatie scoort dicht bij het benchmark gemiddelde op <strong>{effectName}</strong>.
                                                    Er zijn geen grote uitschieters in het profiel. Gerichte aanpassingen in interventie-intensiteit of doelgroepfocus kunnen de score beïnvloeden.
                                                </>
                                            )}
                                            {deltaVsBenchmark < -5 && (
                                                <>
                                                    Deze organisatie scoort onder de benchmark op <strong>{effectName}</strong>.
                                                    {dominantEffort === 'low' && ' Het profiel toont overwegend kortdurende, lichte interventies. Organisaties die hoger scoren, bieden vaak langduriger trajecten of intensievere begeleiding.'}
                                                    {dominantEffort === 'medium' && ' Vergelijking met top performers kan aanknopingspunten geven voor welke aanpassingen het meest effect hebben.'}
                                                    {dominantEffort === 'high' && ' Ondanks hoge intensiteit blijft de score achter. Doelgroepcontext of meetmethode kunnen een rol spelen.'}
                                                </>
                                            )}
                                        </p>

                                        <div className='bcd-ai-disclaimer'>
                                            <span>⚠ Dit is een automatisch gegenereerde richtinggevende analyse, geen causale conclusie.</span>
                                            <span className='bcd-ai-coming-soon'>
                                                Uitgebreide AI-analyse met peer vergelijking en aanbevelingen — binnenkort beschikbaar
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {deltaVsBenchmark == null && (
                                    <p className='bcd-empty'>Onvoldoende data voor automatische analyse.</p>
                                )}
                            </div>

                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default BenchmarkCompanyDetail
